import Page from "@/components/layout/Page";
import * as Styled from "./styled";
import Card from "@/components/shared-ui/Card";
import LoadingScreen from "@/components/shared-ui/LoadingScreen";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import useApi from "@/contexts/ApiContext";
import { Link } from "react-router-dom";
import { useState } from "react";
import type { Update, UpdateAvailableMonth } from "@/services/api/types";
import { Badge } from "../Dashboard/UpdatesCard/styled";
import useCompany from "@/contexts/CompanyContext";
import config from "@/config";
import { UPDATE_CATEGORY_LABELS } from "../Updates/updateConstants";

const UpdatesList = () => {
  const api = useApi();
  const { companyStatus } = useCompany();
  const [categoryFilter, setCategoryFilter] = useState<Update["category"]>();
  const [monthFilter, setMonthFilter] = useState<UpdateAvailableMonth>();
  const [statusFilter, setStatusFilter] = useState<
    "unpublished" | "published"
  >();
  const isLiveCustomer = companyStatus === "live";
  const isStaging = config.NETLIFY_CONTEXT !== "production";

  const { data: updates } = useQuery({
    queryKey: ["get-updates", companyStatus, categoryFilter, monthFilter, statusFilter],
    queryFn: () =>
      api.update.getAll(companyStatus, undefined, categoryFilter, monthFilter, statusFilter),
    placeholderData: keepPreviousData,
  });
  const { data: availableMonths } = useQuery({
    queryKey: ["get-updates-available-months", companyStatus],
    queryFn: () => api.update.getAvailableMonths(companyStatus),
  });

  if (!updates || !availableMonths) {
    return <LoadingScreen />;
  }

  const categories: Update["category"][] = [
    "release_notes",
    "general_notification",
    ...(isLiveCustomer ? ["urgent_notification" as const] : []),
    "roadmap_update",
  ];

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  return (
    <Page title="Updates" searchIndexPriority="updates">
      <Styled.UpdatesList>
        <Card noPadding>
          <Styled.Heading>Important updates</Styled.Heading>
          <Styled.Layout>
            <Styled.List>
              {updates.length < 1 && (
                <span>No results matching these filters</span>
              )}
              {updates.map((item) => (
                <Link key={item.id} to={`/updates/${item.slug}`}>
                  <Styled.ListItem $category={item.category}>
                    <Styled.ListItemTitle>
                      <strong>{item.title}</strong>
                      <Badge>
                        {UPDATE_CATEGORY_LABELS[item.category] ?? item.category}
                      </Badge>
                    </Styled.ListItemTitle>
                    <Styled.Date>
                      {new Date(item.date).toLocaleDateString("en-GB", {
                        year: "numeric",
                        month: "short",
                        day: "2-digit",
                      })}
                    </Styled.Date>
                    <span>{item.description}</span>
                  </Styled.ListItem>
                </Link>
              ))}
            </Styled.List>
            <Styled.FilterBar data-testid={`categoryFilter`}>
              <Styled.FilterHeading>Filter</Styled.FilterHeading>
              {isStaging && (
                <>
                  <Styled.FilterSectionHeading>
                    By status
                  </Styled.FilterSectionHeading>
                  {(["unpublished", "published"] as const).map((status) => (
                    <Styled.FilterItem
                      key={status}
                      $active={statusFilter === status}
                      onClick={() =>
                        setStatusFilter((prev) =>
                          prev === status ? undefined : status
                        )
                      }
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Styled.FilterItem>
                  ))}
                </>
              )}
              <Styled.FilterSectionHeading>
                By category
              </Styled.FilterSectionHeading>
              {categories.map((category) => (
                <Styled.FilterItem
                  key={category}
                  $category={category}
                  $active={categoryFilter === category}
                  onClick={() =>
                    setCategoryFilter((prev) =>
                      prev === category ? undefined : category
                    )
                  }
                >
                  {UPDATE_CATEGORY_LABELS[category]}
                </Styled.FilterItem>
              ))}
              <Styled.FilterSectionHeading>
                By month
              </Styled.FilterSectionHeading>
              {[...availableMonths].reverse().map((month) => (
                <Styled.FilterItem
                  key={`${month.dateMonth}-${month.dateYear}`}
                  $active={month === monthFilter}
                  onClick={() =>
                    setMonthFilter((prev) =>
                      prev === month ? undefined : month
                    )
                  }
                >
                  {months[month.dateMonth - 1]} {month.dateYear}
                </Styled.FilterItem>
              ))}
            </Styled.FilterBar>
          </Styled.Layout>
        </Card>
      </Styled.UpdatesList>
    </Page>
  );
};

export default UpdatesList;
